/**
 * Blueprint Config API Service
 * =============================
 * Central configuration system for SlabReality.
 * All frontend content, UI structure, and logic parameters come from here.
 * 
 * The frontend is a PURE RENDERER - it does not decide anything.
 * Everything comes from Blueprint (Supabase DB).
 * 
 * NO FALLBACK - If DB fails, throw error.
 */

import { supabase, isSupabaseConfigured } from '../lib/supabase';

// Default language
const DEFAULT_LANG = 'it';

/**
 * Convert flat dot-notation keys to nested object
 * e.g., { 'ui.home.sections': {...} } -> { ui: { home: { sections: {...} } } }
 */
const flatToNested = (flatObj) => {
  if (!flatObj || typeof flatObj !== 'object') {
    return {};
  }
  
  const result = {};
  
  for (const [key, value] of Object.entries(flatObj)) {
    const parts = key.split('.');
    let current = result;
    
    for (let i = 0; i < parts.length - 1; i++) {
      if (!(parts[i] in current)) {
        current[parts[i]] = {};
      }
      current = current[parts[i]];
    }
    
    // Parse JSON value if it's a string
    let parsedValue = value;
    if (typeof value === 'string') {
      try {
        parsedValue = JSON.parse(value);
      } catch {
        parsedValue = value;
      }
    }
    
    current[parts[parts.length - 1]] = parsedValue;
  }
  
  return result;
};

/**
 * Sanitize config to ensure {text, route} objects don't break React rendering
 * Converts special objects to proper format where needed
 */
const sanitizeConfig = (obj) => {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }
  
  // Don't sanitize arrays directly, just their contents
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeConfig(item));
  }
  
  const result = {};
  
  for (const [key, value] of Object.entries(obj)) {
    // Check if this is a {text, route} object that should stay as-is (for links)
    // These are valid structures, just need to be rendered correctly by components
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // If it has only text/route keys, it's a link object - keep it
      const keys = Object.keys(value);
      const isLinkObject = keys.length <= 3 && 
        (keys.includes('text') || keys.includes('label')) && 
        (keys.includes('route') || keys.includes('url') || keys.includes('link'));
      
      if (isLinkObject) {
        // Keep link objects as-is but ensure they have both text and route
        result[key] = {
          text: value.text || value.label || key,
          route: value.route || value.url || value.link || '#'
        };
      } else {
        // Recursively sanitize nested objects
        result[key] = sanitizeConfig(value);
      }
    } else {
      result[key] = value;
    }
  }
  
  return result;
};

/**
 * Get full Blueprint configuration for a language
 * Uses Supabase RPC function: get_blueprint_config(lang)
 * 
 * NO FALLBACK - throws on error
 * 
 * @param {string} lang - Language code (it, en, es)
 * @returns {Promise<Object>} Nested configuration object
 */
export const getBlueprintConfig = async (lang = DEFAULT_LANG) => {
  console.log('🧠 getBlueprintConfig() - loading from Supabase RPC');
  
  // Check if Supabase is configured
  if (!isSupabaseConfigured) {
    console.warn('⚠️ Supabase not configured - returning empty config');
    return {};
  }
  
  try {
    // Call RPC function
    const { data, error } = await supabase.rpc('get_blueprint_config', { p_lang: lang });
    
    if (error) {
      console.error('❌ getBlueprintConfig RPC error:', error);
      // Return empty config instead of throwing - allows app to render with defaults
      return {};
    }
    
    if (!data) {
      console.warn('⚠️ getBlueprintConfig returned null - using empty config');
      return {};
    }
    
    console.log('✅ Blueprint config loaded successfully', data);
    
    // Sanitize and return
    return sanitizeConfig(data);
    
  } catch (err) {
    console.error('❌ getBlueprintConfig exception:', err);
    return {};
  }
};

/**
 * Get a specific config value by key path
 * 
 * @param {string} keyPath - Dot-notation path (e.g., 'homepage.hero.title')
 * @param {string} lang - Language code
 * @returns {Promise<any>} Config value
 */
export const getConfigValue = async (keyPath, lang = DEFAULT_LANG) => {
  const config = await getBlueprintConfig(lang);
  return getNestedValue(config, keyPath);
};

/**
 * Get nested value from object using dot notation
 * 
 * @param {Object} obj - Source object
 * @param {string} path - Dot-notation path
 * @returns {any} Value at path or undefined
 */
export const getNestedValue = (obj, path) => {
  if (!obj || !path) return undefined;
  
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
};

/**
 * Get UI configuration (section visibility, theme, etc.)
 * 
 * @param {string} lang - Language code
 * @returns {Promise<Object>} UI configuration
 */
export const getUIConfig = async (lang = DEFAULT_LANG) => {
  const config = await getBlueprintConfig(lang);
  return config?.ui || {};
};

/**
 * Get SRE configuration (scoring weights, thresholds, explanations)
 * 
 * @param {string} lang - Language code
 * @returns {Promise<Object>} SRE configuration
 */
export const getSREConfig = async (lang = DEFAULT_LANG) => {
  const config = await getBlueprintConfig(lang);
  return config?.sre || {};
};

/**
 * Get homepage configuration
 * 
 * @param {string} lang - Language code
 * @returns {Promise<Object>} Homepage configuration
 */
export const getHomepageConfig = async (lang = DEFAULT_LANG) => {
  const config = await getBlueprintConfig(lang);
  return config?.homepage || {};
};

/**
 * Get navbar configuration
 * 
 * @param {string} lang - Language code
 * @returns {Promise<Object>} Navbar configuration
 */
export const getNavbarConfig = async (lang = DEFAULT_LANG) => {
  const config = await getBlueprintConfig(lang);
  return config?.navbar || {};
};

/**
 * Get footer configuration
 * 
 * @param {string} lang - Language code
 * @returns {Promise<Object>} Footer configuration
 */
export const getFooterConfig = async (lang = DEFAULT_LANG) => {
  const config = await getBlueprintConfig(lang);
  return config?.footer || {};
};

/**
 * Check if a section is enabled
 * 
 * @param {string} sectionKey - Section key (e.g., 'hero', 'discovery')
 * @param {Object} config - Full config object
 * @returns {boolean} Whether section is enabled
 */
export const isSectionEnabled = (sectionKey, config) => {
  const sections = config?.ui?.homepage?.sections;
  if (!sections) return true; // Default to enabled
  return sections[sectionKey] !== false;
};

/**
 * Get theme colors from config
 * 
 * @param {Object} config - Full config object
 * @returns {Object} Color tokens
 */
export const getThemeColors = (config) => {
  return config?.ui?.theme?.colors || {
    gold: '#a98865',
    goldLight: '#c4a07a',
    cream: '#F5F2EE',
    dark: '#0D0D0D',
    darkBg: '#080807',
    text1: '#0D0D0D',
    text2: '#444444',
    text3: '#888888',
    border: '#E8E4DE'
  };
};

/**
 * Get theme fonts from config
 * 
 * @param {Object} config - Full config object
 * @returns {Object} Font families
 */
export const getThemeFonts = (config) => {
  return config?.ui?.theme?.fonts || {
    heading: 'Cormorant Garamond, serif',
    body: 'Inter, sans-serif'
  };
};

// ============================================================================
// FALLBACK CONFIG (used when Supabase is not available)
// ============================================================================

const getFallbackConfig = (lang = 'it') => {
  console.warn('⚠️ Using FALLBACK Blueprint config - This should NOT happen in production!');
  console.warn('⚠️ Check: 1) Supabase RPC exists 2) blueprint_config table has data 3) RLS policies');
  
  const isIt = lang === 'it';
  
  // Minimal fallback - should never be used in production
  return {
    navbar: {
      logo: {
        mark: 'SR',
        name: 'SLABREALITY',
        tagline: 'THE REAL SLAB. IN YOUR SPACE.'
      },
      links: [
        { text: 'SRE Engine', url: '/sre' },
        { text: 'Materiali', url: '/materials' },
        { text: 'Ispirazione', url: '/experience/colors' },
        { text: 'Workspace', url: '/workspace' }
      ],
      auth: {
        login: isIt ? 'Accedi / Registrati' : 'Login / Register',
        dashboard: 'Dashboard'
      }
    },
    homepage: {
      hero: {
        label: isIt ? 'SRE ENGINE — PROIEZIONE DETERMINISTICA' : 'SRE ENGINE — DETERMINISTIC PROJECTION',
        title: isIt ? 'La materia reale.\nNel tuo spazio reale.' : 'The real material.\nIn your real space.',
        description: isIt 
          ? 'Niente simulazioni. Niente approssimazioni.\nVedi esattamente ciò che sceglierai.'
          : 'No simulations. No approximations.\nSee exactly what you will choose.',
        cta: isIt ? 'Avvia SRE Engine' : 'Launch SRE Engine',
        ctaLink: '/sre',
        image: 'https://images.unsplash.com/photo-1776935359448-fb11f63c375d?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85',
        badge: {
          top: 'SRE ENGINE',
          bottom: 'Live Preview'
        }
      },
      // Discovery Cards - 3 entry points
      discovery: {
        label: isIt ? 'SCOPRI' : 'DISCOVER',
        cards: [
          {
            id: 'color',
            icon: 'palette',
            title: isIt ? 'ISPIRATI PER COLORE' : 'INSPIRE BY COLOR',
            description: isIt ? 'Esplora materiali raggruppati per famiglia cromatica.' : 'Explore materials grouped by color family.',
            linkText: isIt ? 'Esplora colori' : 'Explore colors',
            link: '/experience/colors',
            image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=500&q=80'
          },
          {
            id: 'similar',
            icon: 'search',
            title: isIt ? 'TROVA SIMILI' : 'FIND SIMILAR',
            description: isIt ? 'Carica un\'immagine e trova materiali corrispondenti.' : 'Upload an image and find matching materials.',
            linkText: isIt ? 'Ricerca visiva' : 'Visual search',
            link: '/visual-search',
            image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=500&q=80'
          },
          {
            id: 'explore',
            icon: 'grid',
            title: isIt ? 'ESPLORA MATERIALI' : 'EXPLORE MATERIALS',
            description: isIt ? 'Sfoglia la nostra collezione completa di lastre.' : 'Browse our complete collection of slabs.',
            linkText: isIt ? 'Vedi catalogo' : 'View catalog',
            link: '/materials',
            image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=500&q=80'
          }
        ]
      },
      // Stats bar
      stats: [
        { icon: 'eye', label: isIt ? '100% Accurato' : '100% Accurate', description: isIt ? 'Proiezione deterministica' : 'Deterministic projection' },
        { icon: 'layers', label: isIt ? 'Lastre Reali' : 'Real Slabs', description: isIt ? 'Non simulazioni' : 'Not simulations' },
        { icon: 'check', label: isIt ? 'Pre-Validato' : 'Pre-Validated', description: isIt ? 'Disponibilità garantita' : 'Guaranteed availability' },
        { icon: 'globe', label: isIt ? 'Network Globale' : 'Global Network', description: isIt ? 'Cave in tutto il mondo' : 'Quarries worldwide' }
      ],
      // Ecosystem section
      ecosystem: {
        label: isIt ? 'ECOSISTEMA' : 'ECOSYSTEM',
        title: isIt ? 'Una piattaforma per ogni professionista' : 'A platform for every professional',
        cards: [
          {
            id: 'architect',
            role: isIt ? 'Architetti & Designer' : 'Architects & Designers',
            subtitle: isIt ? 'PROGETTAZIONE' : 'DESIGN',
            features: [
              isIt ? 'Selezione materiali con SRE Engine' : 'Material selection with SRE Engine',
              isIt ? 'Progetti e moodboard condivisibili' : 'Shareable projects and moodboards',
              isIt ? 'Specifiche tecniche dettagliate' : 'Detailed technical specifications'
            ],
            cta: isIt ? 'Inizia a progettare' : 'Start designing',
            link: '/register',
            image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80'
          },
          {
            id: 'advisor',
            role: 'Material Advisor',
            subtitle: isIt ? 'CONSULENZA' : 'CONSULTING',
            features: [
              isIt ? 'Gestione richieste clienti' : 'Client request management',
              isIt ? 'Proposte materiali personalizzate' : 'Custom material proposals',
              isIt ? 'Dashboard analytics' : 'Analytics dashboard'
            ],
            cta: isIt ? 'Diventa Advisor' : 'Become an Advisor',
            link: '/register',
            image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&q=80'
          },
          {
            id: 'retail',
            role: 'Showroom & Retail',
            subtitle: isIt ? 'VENDITA' : 'SALES',
            features: [
              isIt ? 'Presentazioni immersive ai clienti' : 'Immersive client presentations',
              isIt ? 'Gestione catalogo digitale' : 'Digital catalog management',
              isIt ? 'Integrazione inventario' : 'Inventory integration'
            ],
            cta: isIt ? 'Richiedi demo' : 'Request demo',
            link: '/pricing',
            image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80'
          }
        ]
      },
      // Workspace preview section
      workspace: {
        label: isIt ? 'WORKSPACE' : 'WORKSPACE',
        title: isIt ? 'Gestisci i tuoi\nprogetti' : 'Manage your\nprojects',
        description: isIt ? 'Organizza materiali, crea moodboard e collabora con il tuo team in un unico spazio.' : 'Organize materials, create moodboards and collaborate with your team in one place.',
        link: isIt ? 'Esplora Workspace' : 'Explore Workspace',
        linkUrl: '/workspace',
        backgroundImage: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80',
        mockup: {
          title: isIt ? 'I miei progetti' : 'My projects',
          newButton: isIt ? 'Nuovo' : 'New',
          tabs: [isIt ? 'Attivi' : 'Active', isIt ? 'Archiviati' : 'Archived', isIt ? 'Condivisi' : 'Shared'],
          projects: [
            { name: 'Villa Milano', materials: 12, time: '2h fa', image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=300&q=80' },
            { name: 'Ristorante Roma', materials: 8, time: '1g fa', image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=300&q=80' },
            { name: 'Showroom Firenze', materials: 15, time: '3g fa', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&q=80' }
          ]
        }
      },
      // Featured materials section
      materials: {
        label: isIt ? 'MATERIALI IN EVIDENZA' : 'FEATURED MATERIALS',
        title: isIt ? 'Lastre selezionate\ndai nostri esperti' : 'Slabs selected\nby our experts',
        seeAll: isIt ? 'Vedi tutti' : 'View all',
        seeAllLink: '/materials',
        filters: [
          { id: 'tutti', label: isIt ? 'Tutti' : 'All' },
          { id: 'marmo', label: isIt ? 'Marmo' : 'Marble' },
          { id: 'granito', label: isIt ? 'Granito' : 'Granite' },
          { id: 'onice', label: isIt ? 'Onice' : 'Onyx' }
        ]
      },
      // Network section
      network: {
        label: isIt ? 'NETWORK GLOBALE' : 'GLOBAL NETWORK',
        title: isIt ? 'Materiali da\ntutto il mondo' : 'Materials from\naround the world',
        description: isIt ? 'Accedi a una rete globale di cave e fornitori certificati.' : 'Access a global network of certified quarries and suppliers.',
        link: isIt ? 'Scopri la rete' : 'Discover the network',
        linkUrl: '/materials',
        panel: {
          origin: { label: isIt ? 'Origine' : 'Origin', value: 'Carrara, Italia' },
          availability: { label: isIt ? 'Disponibilità' : 'Availability', value: '24 lastre' },
          finishes: { label: isIt ? 'Finiture' : 'Finishes', value: 'Lucido, Levigato' },
          cta: isIt ? 'Vedi dettagli' : 'View details'
        }
      },
      // CTA section
      cta: {
        title: isIt ? 'Pronto a scoprire\nil tuo prossimo materiale?' : 'Ready to discover\nyour next material?',
        buttons: [
          { text: isIt ? 'Avvia SRE Engine' : 'Launch SRE Engine', link: '/sre', style: 'primary' },
          { text: isIt ? 'Esplora Materiali' : 'Explore Materials', link: '/materials', style: 'ghost' },
          { text: isIt ? 'Contattaci' : 'Contact us', link: '/pricing', style: 'ghost' }
        ]
      }
    },
    footer: {
      tagline: 'THE REAL SLAB. IN YOUR SPACE.',
      description: isIt ? 'La piattaforma di riferimento per la scoperta e selezione di materiali pregiati.' : 'The leading platform for premium material discovery and selection.',
      copyright: '© 2026 SlabReality. Tutti i diritti riservati.',
      columns: [
        {
          title: isIt ? 'Piattaforma' : 'Platform',
          links: [
            { text: isIt ? 'Materiali' : 'Materials', url: '/materials' },
            { text: isIt ? 'SRE Engine' : 'SRE Engine', url: '/sre' },
            { text: isIt ? 'Progetti' : 'Projects', url: '/projects' },
            { text: isIt ? 'Workspace' : 'Workspace', url: '/workspace' }
          ]
        },
        {
          title: isIt ? 'Risorse' : 'Resources',
          links: [
            { text: isIt ? 'Ispirazione' : 'Inspiration', url: '/experience/colors' },
            { text: isIt ? 'Ricerca Visiva' : 'Visual Search', url: '/visual-search' },
            { text: isIt ? 'Collezioni' : 'Collections', url: '/collections' },
            { text: isIt ? 'Trend' : 'Trends', url: '/trends' }
          ]
        },
        {
          title: isIt ? 'Azienda' : 'Company',
          links: [
            { text: 'Pricing', url: '/pricing' },
            { text: isIt ? 'Contatti' : 'Contact', url: '/pricing' },
            { text: 'Privacy Policy', url: '/' },
            { text: 'Termini di Servizio', url: '/' }
          ]
        }
      ],
      newsletter: {
        title: 'Newsletter',
        description: isIt ? 'Ricevi aggiornamenti sui nuovi materiali.' : 'Get updates on new materials.',
        placeholder: isIt ? 'La tua email' : 'Your email'
      }
    },
    // =========================================================================
    // 4-LAYER ARCHITECTURE
    // =========================================================================
    content: {
      common: {
        buttons: {
          back: isIt ? 'Indietro' : 'Back',
          save: isIt ? 'Salva' : 'Save',
          share: isIt ? 'Condividi' : 'Share',
          view_all: isIt ? 'Vedi tutti' : 'View all',
          view_details: isIt ? 'Dettagli' : 'Details',
          compare: isIt ? 'Confronta' : 'Compare',
          save_material: isIt ? 'Salva materiale' : 'Save material',
          launch_sre: isIt ? 'Avvia SRE Engine' : 'Launch SRE Engine',
          request_info: isIt ? 'Richiedi Informazioni' : 'Request Info',
          explore_materials: isIt ? 'Esplora Materiali' : 'Explore Materials'
        },
        states: {
          loading: isIt ? 'Caricamento...' : 'Loading...',
          error: isIt ? 'Errore nel caricamento' : 'Error loading',
          error_retry: isIt ? 'Riprova' : 'Retry',
          empty: isIt ? 'Nessun risultato' : 'No results',
          no_materials: isIt ? 'Nessun materiale trovato' : 'No materials found'
        }
      },
      explorer: {
        header: {
          title: isIt ? 'Esplora Materiali' : 'Explore Materials',
          subtitle: isIt ? 'Scopri la nostra collezione di materiali pregiati' : 'Discover our collection of premium materials'
        },
        results: {
          count: isIt ? '{count} materiali' : '{count} materials',
          count_singular: isIt ? '1 materiale' : '1 material'
        }
      },
      material: {
        detail: {
          gallery_title: isIt ? 'Galleria immagini' : 'Image Gallery',
          error_title: isIt ? 'Materiale non trovato' : 'Material not found',
          error_description: isIt ? 'Il materiale richiesto non esiste o è stato rimosso.' : 'The requested material does not exist or has been removed.',
          specs_title: isIt ? 'Specifiche Rapide' : 'Quick Specs',
          spec_category: isIt ? 'Categoria' : 'Category',
          spec_origin: isIt ? 'Origine' : 'Origin',
          spec_finish: isIt ? 'Finitura' : 'Finish',
          spec_thickness: isIt ? 'Spessore' : 'Thickness',
          spec_supplier: isIt ? 'Fornitore' : 'Supplier',
          cta_title: isIt ? 'Interessato?' : 'Interested?',
          cta_description: isIt ? 'Richiedi informazioni o un campione di questo materiale.' : 'Request information or a sample of this material.'
        },
        dna: {
          title: 'Material DNA',
          subtitle: isIt ? 'Caratteristiche tecniche e visive' : 'Technical and visual characteristics',
          hint: isIt ? 'Il DNA determina la compatibilità con altri materiali' : 'DNA determines compatibility with other materials',
          fields: {
            color_family: { label: isIt ? 'Famiglia Cromatica' : 'Color Family', description: isIt ? 'Tonalità dominante' : 'Dominant tone' },
            tone: { label: isIt ? 'Temperatura' : 'Temperature', description: isIt ? 'Warm, cool o neutro' : 'Warm, cool or neutral' },
            contrast: { label: isIt ? 'Contrasto' : 'Contrast', description: isIt ? 'Livello di variazione' : 'Variation level' },
            pattern_type: { label: 'Pattern', description: isIt ? 'Tipo di venatura/disegno' : 'Vein/pattern type' },
            visual_intensity: { label: isIt ? 'Intensità Visiva' : 'Visual Intensity', description: isIt ? 'Impatto visivo complessivo' : 'Overall visual impact' },
            application_role: { label: isIt ? 'Applicazione' : 'Application', description: isIt ? 'Destinazioni d\'uso consigliate' : 'Recommended uses' }
          }
        },
        applications: {
          title: isIt ? 'Applicazioni Consigliate' : 'Recommended Applications',
          subtitle: isIt ? 'Destinazioni d\'uso ottimali per questo materiale' : 'Optimal uses for this material',
          empty: isIt ? 'Nessuna applicazione specifica suggerita.' : 'No specific application suggested.'
        },
        related: {
          label: 'SRE ENGINE',
          title: isIt ? 'Materiali Correlati' : 'Related Materials',
          description: isIt ? 'Materiali con DNA simile selezionati da SRE Engine' : 'Materials with similar DNA selected by SRE Engine',
          empty: isIt ? 'Nessun materiale correlato trovato.' : 'No related materials found.',
          engine_title: isIt ? 'Come funziona SRE Engine' : 'How SRE Engine works',
          engine_description: isIt 
            ? 'SRE analizza il DNA di ogni materiale (colore, tono, pattern, intensità) per trovare corrispondenze precise. Non è un suggerimento casuale: è matching deterministico basato su caratteristiche reali.'
            : 'SRE analyzes the DNA of each material (color, tone, pattern, intensity) to find precise matches. Not random suggestions: it\'s deterministic matching based on real characteristics.'
        },
        sre_panel: {
          title: 'SRE Intelligence',
          subtitle: isIt ? 'Analisi deterministica del materiale' : 'Deterministic material analysis',
          expand: isIt ? 'Espandi' : 'Expand',
          collapse: isIt ? 'Comprimi' : 'Collapse',
          breakdown_title: 'Score Breakdown',
          matches_title: 'Top Match',
          find_similar: isIt ? 'Trova Simili' : 'Find Similar',
          footer_hint: isIt ? 'Analisi basata su Material DNA • Score 0-100' : 'Analysis based on Material DNA • Score 0-100'
        },
        // SIMILARITY NAVIGATION SYSTEM
        similarity: {
          similar_title: isIt ? 'Materiali Simili' : 'Similar Materials',
          similar_description: isIt ? 'Stesso linguaggio visivo' : 'Same visual language',
          alternatives_title: isIt ? 'Alternative Valide' : 'Strong Alternatives',
          alternatives_description: isIt ? 'Stessa funzione, espressione diversa' : 'Same function, different expression',
          contrast_title: isIt ? 'Alternative a Contrasto' : 'Contrast Alternatives',
          contrast_description: isIt ? 'Tensione progettuale intenzionale' : 'Intentional design tension'
        }
      }
    },
    ui: {
      // =======================================================================
      // ROUTES - Central routing configuration (CRITICAL: no hardcoded routes)
      // =======================================================================
      routes: {
        // Main entry points
        home: '/',
        explorer: '/materials',
        material_detail: '/materials/:slug',
        material_similar: '/materials/similar/:slug',
        sre: '/sre',
        workspace: '/workspace',
        blueprint: '/blueprint',
        studio: '/studio',
        projects: '/projects',
        project_detail: '/projects/:id',
        // Discovery flows
        by_color: '/experience/colors',
        upload_match: '/match',
        visual_search: '/visual-search',
        inspiration: '/project-inspiration',
        // Auth
        login: '/login',
        register: '/register',
        forgot_password: '/forgot-password',
        dashboard: '/dashboard',
        // User
        settings: '/settings',
        notifications: '/notifications',
        subscription: '/account/subscription',
        my_boards: '/my-boards',
        // Compare
        compare: '/compare/materials',
        // Advisor
        advisor: '/advisor',
        // Admin
        admin: '/admin',
        admin_statistics: '/admin/statistics',
        admin_bulk_import: '/admin/bulk-import',
        // Rep
        rep: '/rep',
        // Collections
        collections: '/collections',
        collection_detail: '/collections/:id',
        // Trends
        trends: '/trends',
        my_trending: '/trends/my-materials',
        // Pricing
        pricing: '/pricing',
        // Supplier
        supplier: '/supplier'
      },
      // =======================================================================
      // HOME - Dynamic homepage configuration (CRITICAL: no hardcoded sections)
      // =======================================================================
      home: {
        // Which sections to show (true/false)
        sections: {
          hero: true,
          discovery: true,
          stats: true,
          ecosystem: true,
          workspace: true,
          materials: true,
          network: true,
          cta: true
        },
        // Order of sections (array of section keys)
        order: ['hero', 'discovery', 'stats', 'ecosystem', 'workspace', 'materials', 'network', 'cta']
      },
      // =======================================================================
      // NAVIGATION - What appears in navbar and entry points
      // =======================================================================
      navigation: {
        navbar: {
          links: [
            { key: 'sre', route: 'sre' },
            { key: 'explorer', route: 'explorer' },
            { key: 'inspiration', route: 'by_color' },
            { key: 'projects', route: 'projects' }
          ],
          labels: {
            sre: 'SRE Engine',
            explorer: isIt ? 'Materiali' : 'Materials',
            inspiration: isIt ? 'Ispirazione' : 'Inspiration',
            projects: isIt ? 'Progetti' : 'Projects'
          }
        },
        // 4 Independent Entry Points
        entry_points: {
          discovery: { route: 'explorer', icon: 'grid' },
          match: { route: 'upload_match', icon: 'search' },
          sre: { route: 'sre', icon: 'eye' },
          studio: { route: 'studio', icon: 'palette' }
        }
      },
      // Legacy homepage config (backwards compatibility - use ui.home instead)
      homepage: {
        sections: {
          hero: true,
          discovery: true,
          stats: true,
          ecosystem: true,
          workspace: true,
          materials: true,
          network: true,
          cta: true
        }
      },
      material: {
        detail: {
          show_gallery: true,
          show_dna: true,
          show_applications: true,
          show_related: true,
          show_sre_panel: true,
          show_specs: true
        }
      },
      theme: {
        colors: {
          gold: '#a98865',
          goldLight: '#c4a07a',
          cream: '#F5F2EE',
          dark: '#0D0D0D',
          darkBg: '#080807',
          text1: '#0D0D0D',
          text2: '#444444',
          text3: '#888888',
          border: '#E8E4DE'
        },
        fonts: {
          heading: 'Cormorant Garamond, serif',
          body: 'Inter, sans-serif'
        }
      }
    },
    logic: {
      sre: {
        weights: {
          color_family: 30,
          pattern_type: 25,
          tone: 15,
          application_role: 15,
          visual_intensity: 10,
          contrast: 5
        },
        thresholds: {
          excellent: 80,
          strong: 65,
          medium: 50,
          weak: 30
        },
        labels: {
          excellent: isIt ? 'Match perfetto' : 'Perfect match',
          strong: isIt ? 'Match forte' : 'Strong match',
          medium: isIt ? 'Alternativa valida' : 'Valid alternative',
          weak: isIt ? 'Similitudine parziale' : 'Partial similarity'
        },
        explanations: {
          color_family: isIt ? 'Stessa famiglia cromatica' : 'Same color family',
          pattern_type: isIt ? 'Pattern simile' : 'Similar pattern',
          tone: isIt ? 'Tonalità compatibile' : 'Compatible tone',
          application_role: isIt ? 'Stessa destinazione d\'uso' : 'Same application',
          visual_intensity: isIt ? 'Intensità visiva simile' : 'Similar visual intensity',
          contrast: isIt ? 'Contrasto comparabile' : 'Comparable contrast'
        },
        dna: {
          field_order: ['color_family', 'tone', 'pattern_type', 'visual_intensity', 'contrast', 'application_role']
        },
        ui: {
          reasons_label: isIt ? 'Perché questo match' : 'Why this match'
        }
      },
      applications: {
        labels: {
          bathroom: isIt ? 'Bagno' : 'Bathroom',
          kitchen: isIt ? 'Cucina' : 'Kitchen',
          flooring: isIt ? 'Pavimentazione' : 'Flooring',
          wall: isIt ? 'Rivestimento Pareti' : 'Wall Cladding',
          facade: isIt ? 'Facciata' : 'Facade',
          fireplace: isIt ? 'Camino' : 'Fireplace',
          exterior: isIt ? 'Esterni' : 'Outdoor',
          countertop: isIt ? 'Piano Lavoro' : 'Countertop',
          furniture: isIt ? 'Arredo' : 'Furniture',
          column: isIt ? 'Colonne' : 'Columns',
          staircase: isIt ? 'Scale' : 'Staircase',
          pool: isIt ? 'Piscina' : 'Pool',
          accent: isIt ? 'Accento Decorativo' : 'Decorative Accent'
        },
        descriptions: {
          bathroom: isIt ? 'Ideale per ambienti umidi grazie alla resistenza' : 'Ideal for humid environments',
          kitchen: isIt ? 'Perfetto per piani di lavoro e rivestimenti' : 'Perfect for countertops and cladding',
          flooring: isIt ? 'Durevole e resistente al traffico' : 'Durable and traffic resistant',
          wall: isIt ? 'Elegante soluzione per pareti interne' : 'Elegant solution for interior walls',
          facade: isIt ? 'Resistente agli agenti atmosferici' : 'Weather resistant',
          fireplace: isIt ? 'Resistente al calore e altamente decorativo' : 'Heat resistant and highly decorative',
          exterior: isIt ? 'Adatto per pavimentazioni e rivestimenti outdoor' : 'Suitable for outdoor flooring and cladding',
          countertop: isIt ? 'Superficie ideale per preparazione cibi' : 'Ideal surface for food preparation',
          furniture: isIt ? 'Per elementi d\'arredo su misura' : 'For custom furniture',
          column: isIt ? 'Elemento architettonico di pregio' : 'Premium architectural element',
          staircase: isIt ? 'Eleganza e durabilità per gradini' : 'Elegance and durability for steps',
          pool: isIt ? 'Resistente a cloro e agenti chimici' : 'Resistant to chlorine and chemicals',
          accent: isIt ? 'Elemento decorativo di forte impatto' : 'High-impact decorative element'
        }
      }
    },
    features: {
      explorer: {
        enabled: true,
        filters: ['category', 'color_family', 'pattern_type', 'tone', 'application_role'],
        pagination: { items_per_page: 24 },
        default_view: 'grid'
      },
      material_detail: {
        enabled: true,
        show_dna: true,
        show_applications: true,
        show_related: true,
        show_sre_panel: true,
        related_limit: 4
      }
    },
    // Legacy config (backwards compatibility)
    sre: {
      scoring: {
        weights: {
          color_family: 80,
          color_family_same: 30,
          color_opposite: -40,
          category: 10,
          pattern: 5,
          style_same: 25,
          style_conflict: -20,
          premium_both: 15,
          premium_vs_generic: -5
        },
        threshold: 20
      }
    }
  };
};

export default {
  getBlueprintConfig,
  getConfigValue,
  getNestedValue,
  getUIConfig,
  getSREConfig,
  getHomepageConfig,
  getNavbarConfig,
  getFooterConfig,
  isSectionEnabled,
  getThemeColors,
  getThemeFonts
};
